import React, { useEffect, useMemo, useState } from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import { global } from "@shared/libs/Global";
import { formatNumber, toNum } from "@shared/utils/CommonUtils";
import SnChart from "@shared/components/Grid/SnChart";
import { icoQms01 } from "./icons";

interface Props {
  cdWidget?: string;
  nmWidget?: string;
  searchData?: any;
  refreshKey?: number;
}

const SnSysWidget008: React.FC<Props> = ({
  nmWidget,
  searchData,
  refreshKey,
}) => {
  const { userInfo } = global();
  const [summary, setSummary] = useState<any>({});
  const [trendRows, setTrendRows] = useState<any[]>([]);

  const params = useMemo(
    () => ({
      cd_firm: searchData?.cd_firm || userInfo.cd_firm,
      dt: searchData?.dt,
    }),
    [searchData?.cd_firm, searchData?.dt, userInfo.cd_firm]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data: any = await fnGetDataSet({
          id: "sn_sys_widget_008_01",
          params,
        });

        setSummary(data?.[0]?.[0] || {});
        setTrendRows(data?.[1] || []);
      } catch (error) {
        setSummary({});
        setTrendRows([]);
      }
    };

    fetchData();
  }, [params, refreshKey]);

  const summaryRows = [
    {
      label: "검사수량",
      value: formatNumber(toNum(summary?.qt_qc), { fractionDigits: 0 }),
    },
    {
      label: "합격수량",
      value: formatNumber(toNum(summary?.qt_qc_pass), { fractionDigits: 0 }),
    },
    {
      label: "불량수량",
      value: formatNumber(toNum(summary?.qt_qc_bad), { fractionDigits: 0 }),
    },
    {
      label: "합격률",
      value: `${formatNumber(toNum(summary?.rt_qc_pass), {
        fractionDigits: 1,
      })}%`,
    },
  ];

  const chartData = useMemo(
    () =>
      trendRows.map((row) => ({
        ...row,
        cd_week: row?.cd_week || "",
        nm_week: row?.nm_week || row?.cd_week || "",
        rt_qc_pass: toNum(row?.rt_qc_pass),
      })),
    [trendRows]
  );

  return (
    <div className="flex h-full min-h-[280px] w-full flex-col rounded-lg border border-gray-100 bg-white px-4 py-3 shadow-sm">
      <div className="mb-3 flex items-center gap-3">
        <div className="aspect-square h-10 shrink-0">
          <img src={icoQms01} alt="" className="h-full w-full object-contain" />
        </div>
        <div className="truncate text-xl font-bold text-slate-900">{nmWidget}</div>
      </div>

      <div className="grid grid-cols-4 overflow-hidden rounded border border-slate-300 text-center text-sm font-semibold text-slate-900">
        {summaryRows.map((row) => (
          <React.Fragment key={row.label}>
            <div className="border-b border-r border-slate-300 bg-slate-50 px-2 py-1 last:border-r-0">
              {row.label}
            </div>
          </React.Fragment>
        ))}
        {summaryRows.map((row, index) => (
          <div
            key={`${row.label}-value`}
            className={`px-2 py-1 text-right ${
              index < summaryRows.length - 1 ? "border-r border-slate-300" : ""
            }`}
          >
            {row.value}
          </div>
        ))}
      </div>

      <div className="mt-3 text-sm font-semibold text-slate-800">
        합격률추이
      </div>
      <div className="min-h-0 flex-1">
        <SnChart
          type="custom"
          data={chartData}
          height="100%"
          width="100%"
          xKey="cd_week"
          yKey="rt_qc_pass"
          seriesOptions={{
            series: [
              {
                type: "bar",
                direction: "vertical",
                xKey: "cd_week",
                yKey: "rt_qc_pass",
                yName: "합격률",
                fill: "#3b82f6",
                stroke: "#2563eb",
                tooltip: {
                  renderer: ({ datum, yKey }: any) => ({
                    title: datum?.nm_week || datum?.cd_week || "",
                    content: `${formatNumber(toNum(datum?.[yKey]), {
                      fractionDigits: 1,
                    })}%`,
                  }),
                },
              },
            ],
          }}
          options={{
            padding: { top: 6, right: 10, bottom: 0, left: 6 },
            legend: { enabled: false },
            axes: [
              {
                type: "category",
                position: "bottom",
                label: { fontSize: 12 },
              },
              {
                type: "number",
                position: "left",
                label: { enabled: false },
                nice: true,
              },
            ],
          }}
        />
      </div>
    </div>
  );
};

export default SnSysWidget008;
